import { useEffect, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  Crown,
  LayoutGrid,
  Maximize,
  MessageSquare,
  Mic,
  MicOff,
  MonitorOff,
  MonitorUp,
  MoreHorizontal,
  PhoneOff,
  PictureInPicture2,
  ScreenShare,
  Settings,
  SlidersHorizontal,
  Smile,
  Sparkles,
  Trophy,
  UserPlus,
  Video,
  VideoOff,
  Volume2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Avatar, Badge, Button, Modal } from '@/components/ui';
import { ROUTES } from '@/config/constants';
import { toast } from '@/components/feedback/toast';
import { useVoice } from '../voice/VoiceProvider';
import type {
  VoiceFrameRate,
  VoicePeer,
  VoiceVideoHeight,
  VoiceVideoQuality,
} from '../hooks/useVoiceRoom';
import type { Channel } from './channels';

interface Props {
  communityId: string;
  channel: Channel;
  /** The signed-in user, shown as their own tile once "connected". */
  self: { id: string; name: string; avatarSrc?: string | null } | null;
  onInvite: () => void;
  onDisconnect: () => void;
  onOpenChat: () => void;
}

// Control bar — bare floating icons, no fills. State reads through color alone.
const GROUP = 'flex items-center';
const CTRL =
  'grid h-10 w-10 place-items-center rounded-full border-0 bg-transparent text-white/70 transition-all duration-150 hover:text-white active:scale-90';
const CTRL_ACTIVE = 'text-accent hover:text-accent';
const CTRL_DANGER = 'text-[#ff6b6e] hover:text-[#ff8e90]';
const CHEVRON =
  'grid h-10 w-4 place-items-center border-0 bg-transparent text-white/40 transition-colors duration-150 hover:text-white';
const CORNER =
  'grid h-10 w-10 place-items-center rounded-[10px] border-0 bg-transparent text-white/45 transition-colors duration-150 hover:text-white';
const QUALITY_HEIGHTS = [720, 1080, 1440] as const;
const QUALITY_FPS = [15, 30, 60] as const;

const qualityNeedsWinrPlus = (quality: VoiceVideoQuality) =>
  quality.height > 720 || quality.frameRate > 30;
const qualityLabel = (quality: VoiceVideoQuality) => `${quality.height}p / ${quality.frameRate}fps`;

/**
 * Voice-channel "stage" — the screen you drop into when you click a voice
 * channel (the cv2 analogue of Discord's call view). Participant tiles fill the
 * stage (camera video when on, avatar otherwise); a screen share spotlights to
 * the top; an activity promo invites others while the room is quiet; the bottom
 * bar carries the live mic / camera / screen-share / disconnect controls.
 *
 * Wired to {@link useVoiceRoom}: Supabase Presence for the roster + state and a
 * WebRTC mesh for the audio/camera/screen media.
 */
export function VoiceStage({
  communityId,
  channel,
  self,
  onInvite,
  onDisconnect,
  onOpenChat,
}: Props) {
  const navigate = useNavigate();
  const {
    session,
    join,
    leave,
    setStageOpen,
    participants,
    muted,
    cameraOn,
    sharing,
    videoQuality,
    saveVideoQuality,
    isWinrPlus,
    status,
    error,
    toggleMuted,
    toggleCamera,
    toggleSharing,
  } = useVoice();
  const [moreOpen, setMoreOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [draftQuality, setDraftQuality] = useState<VoiceVideoQuality>(videoQuality);

  // Opening a voice channel joins it (Discord-style). Switching to a different
  // voice channel moves the call. The session lives in the app-level provider,
  // so it keeps running when you navigate away — only the hangup button leaves.
  useEffect(() => {
    if (!self) return;
    if (session?.communityId === communityId && session?.channelId === channel.id) return;
    join({
      communityId,
      channelId: channel.id,
      channelName: channel.name,
      self,
    });
  }, [self, session, communityId, channel.id, channel.name, join]);

  // Tell the dock to hide while this full stage is on screen.
  useEffect(() => {
    setStageOpen(true);
    return () => setStageOpen(false);
  }, [setStageOpen]);

  useEffect(() => {
    if (moreOpen) setDraftQuality(videoQuality);
  }, [moreOpen, videoQuality]);

  const handleDisconnect = () => {
    leave();
    onDisconnect();
  };

  // Invite people into the call: copy a shareable link and confirm it. (A
  // direct per-member "ring" invite can layer on top of this later.)
  const handleInvite = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast('Invite link copied — share it to pull people into voice.', 'ok');
    } catch {
      toast('Could not copy the invite link.', 'error');
    }
    onInvite();
  };

  const saveQuality = async () => {
    const saved = await saveVideoQuality(draftQuality);
    if (!saved) {
      setUpgradeOpen(true);
      return;
    }
    toast(`Call quality set to ${qualityLabel(draftQuality)}.`, 'ok');
    setMoreOpen(false);
  };

  const openWinrPlus = () => {
    setUpgradeOpen(false);
    navigate(ROUTES.pricing);
  };

  const draftNeedsPlus = qualityNeedsWinrPlus(draftQuality) && !isWinrPlus;
  const screenSharer = participants.find((p) => p.screenStream);
  const showPromo = !screenSharer && participants.length <= 1;

  // Tile grid stays comfortable up to 4 across, then wraps.
  const cols =
    participants.length <= 1
      ? 'grid-cols-1'
      : participants.length <= 4
        ? 'grid-cols-2'
        : 'grid-cols-3';

  return (
    <>
      {/* Header — mirrors the ChatPane channel header, with a chat toggle on the right. */}
      <div className="flex h-14 flex-none items-center gap-3.5 border-b border-white/[0.05] px-[18px]">
        <span className="inline-flex items-center text-[#4ade80]">
          <Volume2 size={18} strokeWidth={2} />
        </span>
        <span className="text-[15px] font-extrabold tracking-[-0.2px] text-[#fafafa]">
          {channel.name}
        </span>
        {status === 'connecting' && (
          <span className="text-[12px] font-semibold text-white/[0.46]">Connecting…</span>
        )}
        {error && (
          <span className="truncate text-[12px] font-semibold text-[#ffb4b6]">{error}</span>
        )}
        <button
          className="ml-auto grid h-9 w-9 place-items-center rounded-[10px] border border-transparent bg-transparent text-white/[0.72] transition-all duration-150 hover:border-white/[0.05] hover:bg-white/[0.06] hover:text-[#fafafa]"
          title="Channel chat"
          aria-label="Channel chat"
          onClick={onOpenChat}
        >
          <MessageSquare size={18} />
        </button>
      </div>

      {/* Stage */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-5">
          <div className="mx-auto flex h-full w-full max-w-[940px] flex-col justify-center gap-3">
            {/* Screen-share spotlight */}
            {screenSharer?.screenStream && (
              <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-accent/40 bg-black shadow-[0_0_0_1px_rgba(84,185,254,0.25)]">
                <StreamVideo stream={screenSharer.screenStream} className="object-contain" />
                <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-pill bg-black/55 px-2.5 py-1 backdrop-blur-sm">
                  <ScreenShare size={13} strokeWidth={2.4} className="text-accent" />
                  <span className="text-[13px] font-semibold text-[#fafafa]">
                    {screenSharer.self ? 'Your screen' : `${screenSharer.name}'s screen`}
                  </span>
                </div>
              </div>
            )}

            {participants.length > 0 && (
              <div className={cn('grid gap-3', screenSharer ? 'grid-cols-3 sm:grid-cols-4' : cols)}>
                {participants.map((p) => (
                  <ParticipantTile key={p.connId} participant={p} compact={Boolean(screenSharer)} />
                ))}
              </div>
            )}

            {/* Activity / invite promo — Discord's quiet-room card. */}
            {showPromo && (
              <div className="relative flex aspect-[16/7] w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-white/[0.05] bg-[radial-gradient(120%_140%_at_50%_-10%,rgba(124,92,252,0.35),rgba(12,12,18,0.6)_60%)]">
                <div className="mb-5 flex items-end gap-3 opacity-90 drop-shadow-[0_10px_30px_rgba(124,92,252,0.45)]">
                  <span className="grid h-14 w-14 -rotate-12 place-items-center rounded-2xl bg-[linear-gradient(150deg,#c084fc,#7c5cfc)] text-white shadow-[0_8px_24px_rgba(124,92,252,0.5)]">
                    <Trophy size={26} strokeWidth={2} />
                  </span>
                  <span className="grid h-16 w-16 place-items-center rounded-2xl bg-[linear-gradient(150deg,#f0abfc,#c084fc)] text-white shadow-[0_8px_24px_rgba(192,132,252,0.5)]">
                    <Sparkles size={30} strokeWidth={2} />
                  </span>
                  <span className="grid h-14 w-14 rotate-12 place-items-center rounded-2xl bg-[linear-gradient(150deg,#67e8f9,#22d3ee)] text-white shadow-[0_8px_24px_rgba(34,211,238,0.45)]">
                    <LayoutGrid size={26} strokeWidth={2} />
                  </span>
                </div>
                <div className="flex items-center gap-2.5">
                  <button
                    className="flex h-9 items-center gap-2 rounded-[10px] border-0 bg-white/[0.08] px-4 text-[13px] font-bold text-[#fafafa] transition-all duration-150 hover:bg-white/[0.14]"
                    onClick={() => void handleInvite()}
                  >
                    <UserPlus size={16} strokeWidth={2.2} /> Invite to Voice
                  </button>
                  <button
                    className="flex h-9 items-center gap-2 rounded-[10px] border-0 bg-white/[0.08] px-4 text-[13px] font-bold text-[#fafafa] transition-all duration-150 hover:bg-white/[0.14]"
                    title="Activities coming soon"
                  >
                    <LayoutGrid size={16} strokeWidth={2.2} /> Choose Activity
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Control bar — centered, fill-free floating icons */}
        <div className="relative px-4 pb-5 pt-2">
          <div className="flex items-center justify-center gap-4">
            {/* Mic */}
            <div className={GROUP}>
              <button
                className={cn(CTRL, muted && CTRL_DANGER)}
                title={muted ? 'Unmute' : 'Mute'}
                aria-pressed={muted}
                onClick={toggleMuted}
              >
                {muted ? <MicOff size={20} /> : <Mic size={20} />}
              </button>
              <button className={CHEVRON} title="Mic settings" aria-label="Mic settings">
                <ChevronDown size={13} />
              </button>
            </div>

            {/* Camera */}
            <div className={GROUP}>
              <button
                className={cn(CTRL, cameraOn && CTRL_ACTIVE)}
                title={cameraOn ? 'Turn off camera' : 'Turn on camera'}
                aria-pressed={cameraOn}
                onClick={toggleCamera}
              >
                {cameraOn ? <Video size={20} /> : <VideoOff size={20} />}
              </button>
              <button className={CHEVRON} title="Camera settings" aria-label="Camera settings">
                <ChevronDown size={13} />
              </button>
            </div>

            {/* Screen share / invite / activity / soundboard / more */}
            <div className={cn(GROUP, 'gap-1')}>
              <button
                className={cn(CTRL, sharing && CTRL_ACTIVE)}
                title={sharing ? 'Stop sharing' : 'Share your screen'}
                aria-pressed={sharing}
                onClick={toggleSharing}
              >
                {sharing ? <MonitorOff size={20} /> : <MonitorUp size={20} />}
              </button>
              <button
                className={CTRL}
                title="Invite people"
                aria-label="Invite people"
                onClick={() => void handleInvite()}
              >
                <UserPlus size={20} />
              </button>
              <button className={CTRL} title="Activities (coming soon)">
                <LayoutGrid size={20} />
              </button>
              <button className={CTRL} title="Soundboard (coming soon)">
                <Smile size={20} />
              </button>
              <div className="relative">
                <button
                  className={cn(CTRL, moreOpen && CTRL_ACTIVE)}
                  title="More"
                  aria-label="More call settings"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen((open) => !open)}
                >
                  <MoreHorizontal size={20} />
                </button>
                {moreOpen && (
                  <CallSettingsMenu
                    cameraOn={cameraOn}
                    draftQuality={draftQuality}
                    isWinrPlus={isWinrPlus}
                    onDraftQualityChange={setDraftQuality}
                    onSaveQuality={() => void saveQuality()}
                    qualityNeedsPlus={draftNeedsPlus}
                    savedQuality={videoQuality}
                  />
                )}
              </div>
            </div>

            {/* Disconnect — the one solid affordance, set apart from the icons */}
            <button
              className="group ml-1 grid h-11 w-11 place-items-center rounded-full border-0 bg-[#ed4245] text-white shadow-[0_6px_20px_rgba(237,66,69,0.45)] transition-all duration-150 hover:bg-[#f04747] hover:shadow-[0_8px_26px_rgba(237,66,69,0.6)] active:scale-90"
              title="Disconnect"
              aria-label="Disconnect"
              onClick={handleDisconnect}
            >
              <PhoneOff
                size={19}
                strokeWidth={2.4}
                className="transition-transform duration-150 group-hover:rotate-[135deg]"
              />
            </button>
          </div>

          {/* Corner utilities */}
          <div className="absolute bottom-5 right-4 flex items-center gap-1">
            <button className={CORNER} title="Pop out">
              <PictureInPicture2 size={18} />
            </button>
            <button className={CORNER} title="Full screen">
              <Maximize size={18} />
            </button>
          </div>
        </div>
      </div>

      <WinrPlusPaywallModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        onUpgrade={openWinrPlus}
      />
    </>
  );
}

/** A <video> bound to a live MediaStream. Always muted — audio plays through the
 *  hook's dedicated audio elements, so tile/spotlight video never double-plays. */
function StreamVideo({
  stream,
  mirror,
  className,
}: {
  stream: MediaStream;
  mirror?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.srcObject = stream;
    void v.play().catch(() => {});
    // Browsers pause <video> for backgrounded tabs and don't always resume on
    // return — re-play when the tab becomes visible so the camera never appears
    // to "turn off" after a tab switch.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void v.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted
      className={cn('h-full w-full object-cover', mirror && 'scale-x-[-1]', className)}
    />
  );
}

interface CallSettingsMenuProps {
  cameraOn: boolean;
  draftQuality: VoiceVideoQuality;
  isWinrPlus: boolean;
  onDraftQualityChange: (quality: VoiceVideoQuality) => void;
  onSaveQuality: () => void;
  qualityNeedsPlus: boolean;
  savedQuality: VoiceVideoQuality;
}

function CallSettingsMenu({
  cameraOn,
  draftQuality,
  isWinrPlus,
  onDraftQualityChange,
  onSaveQuality,
  qualityNeedsPlus,
  savedQuality,
}: CallSettingsMenuProps) {
  const heightIndex = QUALITY_HEIGHTS.indexOf(draftQuality.height);
  const fpsIndex = QUALITY_FPS.indexOf(draftQuality.frameRate);
  const unchanged =
    draftQuality.height === savedQuality.height &&
    draftQuality.frameRate === savedQuality.frameRate;

  const setHeight = (height: VoiceVideoHeight) => onDraftQualityChange({ ...draftQuality, height });
  const setFrameRate = (frameRate: VoiceFrameRate) =>
    onDraftQualityChange({ ...draftQuality, frameRate });

  return (
    <div className="absolute bottom-12 right-0 z-40 w-[min(360px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-white/[0.10] bg-[#11131b]/95 text-left shadow-[0_20px_70px_rgba(0,0,0,0.55)] backdrop-blur-2xl">
      <div className="flex items-center justify-between border-b border-white/[0.07] px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="grid h-8 w-8 place-items-center rounded-[10px] bg-white/[0.07] text-accent">
            <Settings size={16} strokeWidth={2.3} />
          </span>
          <div>
            <div className="text-[13px] font-black text-[#fafafa]">Voice & Video</div>
            <div className="text-[11px] font-semibold text-white/45">
              Current: {qualityLabel(savedQuality)}
            </div>
          </div>
        </div>
        <Badge tone={isWinrPlus ? 'accent' : 'neutral'}>{isWinrPlus ? 'WINR+' : 'Free'}</Badge>
      </div>

      <div className="space-y-4 px-4 py-4">
        <div className="rounded-xl border border-white/[0.07] bg-white/[0.035] p-3.5">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.18em] text-white/50">
                <SlidersHorizontal size={14} /> Video quality
              </div>
              <div className="mt-1 text-sm font-extrabold text-[#fafafa]">
                {qualityLabel(draftQuality)}
              </div>
            </div>
            {qualityNeedsPlus ? (
              <Badge tone="warning">WINR+</Badge>
            ) : (
              <Badge tone="success">Allowed</Badge>
            )}
          </div>

          <QualitySlider
            label="HD"
            max={QUALITY_HEIGHTS.length - 1}
            value={heightIndex}
            values={QUALITY_HEIGHTS.map((height) => String(height) + 'p')}
            onChange={(index) => setHeight(QUALITY_HEIGHTS[index] ?? 720)}
          />
          <QualitySlider
            label="FPS"
            max={QUALITY_FPS.length - 1}
            value={fpsIndex}
            values={QUALITY_FPS.map((fps) => String(fps))}
            onChange={(index) => setFrameRate(QUALITY_FPS[index] ?? 30)}
          />

          <div className="mt-3 flex items-center gap-2 rounded-[10px] bg-black/25 px-3 py-2 text-[11px] font-semibold text-white/55">
            <Crown size={13} className={qualityNeedsPlus ? 'text-amber-300' : 'text-white/35'} />
            <span>
              Free calls stay capped at 720p and 30fps. WINR+ unlocks 1080p, 1440p, and 60fps.
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <CallSettingTile title="Camera" value={cameraOn ? 'On' : 'Off'} active={cameraOn} />
          <CallSettingTile title="Input mode" value="Voice activity" active />
          <CallSettingTile title="Noise suppression" value="Standard" active />
          <CallSettingTile title="Echo cancellation" value="On" active />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/[0.07] pt-3">
          <div className="text-[11px] font-semibold text-white/45">
            Saved changes apply to this call and your next call.
          </div>
          <Button
            size="sm"
            variant={qualityNeedsPlus ? 'primary' : 'subtle'}
            disabled={unchanged && !qualityNeedsPlus}
            onClick={onSaveQuality}
          >
            {qualityNeedsPlus ? 'Unlock' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function QualitySlider({
  label,
  max,
  value,
  values,
  onChange,
}: {
  label: string;
  max: number;
  value: number;
  values: string[];
  onChange: (index: number) => void;
}) {
  return (
    <div className="mt-4">
      <div className="mb-1.5 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
        <span>{label}</span>
        <span className="text-white/70">{values[value]}</span>
      </div>
      <input
        aria-label={label}
        type="range"
        min={0}
        max={max}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.currentTarget.value))}
        className="h-2 w-full cursor-pointer accent-accent"
      />
      <div className="mt-1.5 flex justify-between text-[10px] font-bold text-white/35">
        {values.map((option) => (
          <span key={option}>{option}</span>
        ))}
      </div>
    </div>
  );
}

function CallSettingTile({
  title,
  value,
  active,
}: {
  title: string;
  value: string;
  active?: boolean;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.035] px-3 py-2.5">
      <div className="text-[11px] font-bold text-white/45">{title}</div>
      <div className="mt-1 flex items-center gap-1.5 text-[12px] font-extrabold text-[#fafafa]">
        {active && <Check size={12} className="text-accent" />}
        <span>{value}</span>
      </div>
    </div>
  );
}

function WinrPlusPaywallModal({
  open,
  onClose,
  onUpgrade,
}: {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} className="max-w-[460px] overflow-hidden p-0">
      <div className="bg-[radial-gradient(120%_90%_at_50%_0%,rgba(84,185,254,0.28),rgba(15,18,27,0)_62%)] px-7 pb-7 pt-8">
        <div className="mb-5 grid h-14 w-14 place-items-center rounded-2xl bg-accent text-black shadow-[0_18px_50px_var(--winr-accent-soft)]">
          <Crown size={28} strokeWidth={2.4} />
        </div>
        <Badge tone="accent">WINR+</Badge>
        <h2 className="mt-4 text-3xl font-black tracking-[-0.8px] text-white">
          Upgrade call quality.
        </h2>
        <p className="mt-3 text-sm font-semibold leading-6 text-white/62">
          Free voice and video calls are capped at 720p and 30fps. Join WINR+ to save 1080p, 1440p,
          or 60fps call settings.
        </p>

        <div className="mt-5 grid gap-2">
          {[
            '1080p and 1440p camera quality',
            '60fps video calls',
            'Premium call settings saved across rooms',
          ].map((feature) => (
            <div
              key={feature}
              className="flex items-center gap-2 rounded-xl border border-white/[0.07] bg-white/[0.045] px-3 py-2.5 text-sm font-bold text-white/78"
            >
              <Check size={15} className="text-accent" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-3">
          <Button className="flex-1" onClick={onUpgrade}>
            <Crown size={16} /> Join WINR+
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Stay Free
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ParticipantTile({ participant, compact }: { participant: VoicePeer; compact?: boolean }) {
  const { name, avatarSrc, muted, speaking, cameraStream, self } = participant;
  return (
    <div
      className={cn(
        'relative flex aspect-video items-center justify-center overflow-hidden rounded-xl border bg-[#0c0c12] transition-shadow duration-200',
        speaking
          ? 'border-[#4ade80]/70 shadow-[0_0_0_2px_rgba(74,222,128,0.5)]'
          : 'border-white/[0.05]',
      )}
    >
      {cameraStream ? (
        <StreamVideo stream={cameraStream} mirror={self} />
      ) : (
        <div
          className={cn(
            'rounded-full p-1 transition-all duration-200',
            speaking ? 'ring-2 ring-[#4ade80] ring-offset-2 ring-offset-[#0c0c12]' : '',
          )}
        >
          <Avatar src={avatarSrc} name={name} size={compact ? 48 : 84} />
        </div>
      )}

      {/* Name + mute state, bottom-left. */}
      <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 rounded-pill bg-black/50 px-2.5 py-1 backdrop-blur-sm">
        {muted && <MicOff size={13} strokeWidth={2.4} className="text-[#ff6b6e]" />}
        <span
          className={cn('font-semibold text-[#fafafa]', compact ? 'text-[12px]' : 'text-[13px]')}
        >
          {name}
        </span>
      </div>
    </div>
  );
}
